import { Button, SidebarTrigger, TabsList, TabsTrigger, useSidebar } from '@ibsys/kit';
import { LayoutGrid, MessageCirclePlus, MessagesSquare, SidebarIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { IconButton, Tooltip, useMainLayout } from '@/shared/ui';
import { useHasPermission } from '@/entities/profile';
import { useCopilotPanelActions } from '@/widgets/MainLayout/model/hooks/useCopilotPanelActions';
import { useHasOtherSidebarPanels } from '@/widgets/MainLayout/model/hooks/useHasOtherSidebarPanels';

type Props = {
  className?: string;
};

const triggerClassName = cn(
  'size-8 min-h-7 grow-0 text-sidebar-foreground transition-all',
  'data-[state=active]:bg-sidebar-accent data-[state=active]:text-sidebar-accent-foreground',
  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
);

export const PanelsBar: FC<Props> = ({ className }) => {
  const { t } = useTranslation(['common', 'aiAgent']);
  const {
    sidebar: { activePanel },
    isMobile,
  } = useMainLayout();
  const { openMobile } = useSidebar();
  // Кнопки чатов ИИ Агента доступны по разрешению использования ИИ Агента (в т.ч. выданному через роль).
  const canUseAiAgent = useHasPermission('USE_AI_AGENT');
  // Переключатель с единственным вариантом ничего не выбирает
  const hasOtherPanels = useHasOtherSidebarPanels();
  const { handleCreateNewChat, handleShowChatList } = useCopilotPanelActions();

  // Переключатель назван действием, доступным сейчас: открытое меню он сворачивает,
  // свёрнутое — разворачивает
  const toggleMenuLabel = openMobile ? t('common:collapseNavigationMenu') : t('common:expandNavigationMenu');

  return (
    <TabsList
      className={cn(
        'bg-sidebar border-r border-sidebar-border rounded-none flex flex-col items-center justify-start py-4 gap-2 min-h-full',
        className,
      )}
    >
      {isMobile && (
        <Tooltip tooltip={toggleMenuLabel}>
          <SidebarTrigger aria-label={toggleMenuLabel}>
            <SidebarIcon />
          </SidebarTrigger>
        </Tooltip>
      )}
      {hasOtherPanels && (
        <Tooltip tooltip={t('common:navigation')}>
          <TabsTrigger asChild value={'navigation'} className={triggerClassName}>
            <Button
              icon={<LayoutGrid className="h-5 w-5" />}
              variant={activePanel === 'navigation' ? 'secondary' : 'ghost'}
              size="icon"
              aria-label={t('common:navigation')}
            />
          </TabsTrigger>
        </Tooltip>
      )}

      {canUseAiAgent && (
        <IconButton
          icon={<MessageCirclePlus className="h-5 w-5" />}
          variant="ghost"
          size="icon"
          className={cn('size-8 min-h-7', 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground')}
          label={t('aiAgent:newChat')}
          onClick={handleCreateNewChat}
        />
      )}

      {canUseAiAgent && (
        <Tooltip tooltip={t('aiAgent:chatList')}>
          <TabsTrigger asChild value={'copilot'} className={triggerClassName}>
            <Button
              icon={<MessagesSquare className="h-5 w-5" />}
              variant={activePanel === 'copilot' ? 'secondary' : 'ghost'}
              size="icon"
              aria-label={t('aiAgent:chatList')}
              onClick={handleShowChatList}
            />
          </TabsTrigger>
        </Tooltip>
      )}
    </TabsList>
  );
};
